import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';

const PENDING_CHALLENGE_KEY = 'pendingLiveChallenge';

const formatTimeLeft = (endTime, now) => {
    const diff = new Date(endTime).getTime() - now.getTime();
    if (diff <= 0) return 'Ending now';
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    if (days > 0) {
        return `${days}d ${hours}h ${minutes}m ${seconds}s left`;
    }
    if (hours > 0) {
        return `${hours}h ${minutes}m ${seconds}s left`;
    }
    if (minutes > 0) {
        return `${minutes}m ${seconds}s left`;
    }
    return `${seconds}s left`;
};

const ActiveChallengesWidget = () => {
    const [challenges, setChallenges] = useState([]);
    const [loading, setLoading] = useState(true);
    const [now, setNow] = useState(new Date());
    const navigate = useNavigate();

    // Live-ticking timer updating every second
    useEffect(() => {
        const timer = setInterval(() => {
            setNow(new Date());
        }, 1000);
        return () => clearInterval(timer);
    }, []);

    useEffect(() => {
        const fetchChallenges = async () => {
            try {
                const { getAuthToken } = await import('../../../utils/cookies');
                const token = getAuthToken();
                const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

                const res = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8081'}/api/challenges`, { headers, credentials: 'include' });
                if (res.ok) {
                    const data = await res.json();
                    const activeChallenges = data.challenges || [];
                    try {
                        const pendingChallenge = JSON.parse(sessionStorage.getItem(PENDING_CHALLENGE_KEY) || 'null');
                        const isStillLive = pendingChallenge && (!pendingChallenge.end_time || new Date(pendingChallenge.end_time).getTime() > Date.now());
                        if (isStillLive && !activeChallenges.some(challenge => challenge.id === pendingChallenge.id)) {
                            activeChallenges.push(pendingChallenge);
                        }
                    } catch (storageError) {
                        console.warn('Could not restore pending challenge:', storageError);
                    }
                    setChallenges(activeChallenges);
                }
            } catch (err) {
                console.error("Failed to fetch challenges", err);
            } finally {
                setLoading(false);
            }
        };
        fetchChallenges();
    }, []);

    const [joiningId, setJoiningId] = useState(null);

    const handleAction = async (challenge) => {
        try {
            setJoiningId(challenge.id);
            const { getAuthToken } = await import('../../../utils/cookies');
            const token = getAuthToken();
            const headers = token ? { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };

            const res = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8081'}/api/challenges/${challenge.id}/start`, {
                method: 'POST',
                headers,
                credentials: 'include'
            });

            if (res.ok) {
               const data = await res.json();
               sessionStorage.setItem(PENDING_CHALLENGE_KEY, JSON.stringify({ ...challenge, sessionId: data.sessionId }));
               navigate('/pre-interview-check', {
                   state: {
                       sessionId: data.sessionId,
                       config: data.config,
                       isChallenge: true
                   }
               });
            } else {
               const err = await res.json();
               alert(err.error || "Failed to start challenge");
            }
        } catch (err) {
            console.error(err);
            alert("Error starting challenge");
        } finally {
            setJoiningId(null);
        }
    };

    if (loading) {
        return (
            <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-sm animate-pulse">
                <div className="h-5 w-40 bg-slate-200 rounded mb-6"></div>
                <div className="space-y-4">
                    <div className="h-16 bg-slate-100 rounded-2xl"></div>
                    <div className="h-16 bg-slate-100 rounded-2xl"></div>
                </div>
            </div>
        );
    }

    if (challenges.length === 0) return null; // hide widget if no active challenges

    return (
        <div className="bg-[linear-gradient(to_bottom,rgba(16,185,129,0.12),var(--color-card))] rounded-3xl p-6 border-2 border-emerald-700 transition-all duration-300 relative overflow-hidden">
            <div className="flex justify-between items-center mb-5 relative z-10">
                 <h3 className="font-semibold text-sm text-slate-900 flex items-center gap-2">
                     <Icon name="Trophy" size={14} className="text-[#F59E0B]" /> 
                     Live Challenges
                 </h3>
                 <span className="text-white text-[11px] tracking-wide font-semibold px-3 py-1 rounded-full border border-emerald-700 bg-emerald-700">
                     Live
                 </span>
            </div>

            <div className="space-y-3 relative z-10 mt-1">
                {challenges.map(c => {
                    const timeLeftStr = formatTimeLeft(c.end_time, now);
                    
                    return (
                        <div key={c.id} className="bg-emerald-50 border-2 border-emerald-200 rounded-2xl p-4 flex justify-between items-start gap-3 hover:border-emerald-500 transition-colors">
                            <div className="flex flex-col gap-1 leading-tight min-w-0">
                                <h4 className="font-semibold text-sm text-slate-900 truncate">{c.title}</h4>
                                {c.description && (
                                    <p className="text-[11px] font-medium text-slate-700 leading-snug line-clamp-2">{c.description}</p>
                                )}
                                <div className="text-[11px] font-normal text-slate-400 flex items-center gap-1 mt-0.5 font-mono">
                                    <Icon name="Clock" size={11} className="text-slate-400" /> 
                                    {timeLeftStr}
                                </div>
                            </div>
                            <button 
                                onClick={() => handleAction(c)}
                                disabled={joiningId === c.id}
                                className={`shrink-0 bg-[#059669] hover:bg-emerald-700 text-white px-4 py-2 rounded-[12px] text-[12px] font-normal transition-all flex items-center gap-1.5 ${joiningId === c.id ? 'opacity-70 cursor-not-allowed' : 'hover:scale-[1.02] active:scale-[0.98]'}`}
                            >
                                {joiningId === c.id ? (
                                    <>
                                        <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                        Joining...
                                    </>
                                ) : 'Participate'}
                            </button>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default ActiveChallengesWidget;
